import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import shallow from "zustand/shallow";
import { Box, Flex, Tooltip, useDisclosure } from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { UNEXPECTED_ERROR } from "@/utils/envUtils";

import _ from "lodash";
import FlipMove from "react-flip-move";
import moment from "moment";
import url from "url";

import Api from "@/js/api/authorisedApi";
import { createOrUpdateFocus, endFocus, getFocusesAppliedToClass } from "@/js/api/focus";
import { unpauseAllStudents } from "@/api/pause";

import { arrayMove } from "@/js/utils/arrayUtils";
import p2pManager from "@/js/utils/p2pManager";
import { sortUsersByActivity } from "@/js/utils/userUtils";
import { generateSessionStatusTxt } from "@/js/utils/timeUtils";
import { userDisplayText } from "@/js/utils/userUtils";
import * as windowUtils from "@/js/utils/windowUtils";
import { getClassroomIdForChat, isChatFeatureEnabled } from "@/js/utils/chatUtils";

import useStore from "@/stores/index";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { getNativeAppControlsDb, getSignatureDb } from "@/stores/signatureStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useLicenseStore } from "@/stores/v2/licenseStore";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";

import Nav from "@/js/components/Nav";
import BypassModal from "@/js/components/BypassModal";
import { ClassroomPromo } from "@/js/components/ClassroomPromo";
import ClassroomStartSession from "@/js/components/ClassroomStartSession";
import CreatePolicy from "@/js/components/CreatePolicy";
import EditClassButton from "@/js/components/EditClassButton";
import EndClassButton from "@/js/components/EndClassButton";
import ErrorBoundary from "@/components/ErrorBoundary";
import ClassroomEditSession from "@/js/components/ClassroomEditSession";
import RegularClassroomUser from "@/js/components/RegularClassroomUser";
import ClassroomChat from "@/js/components/ClassroomChat";
import P2PDisplayModal from "@/js/components/P2PDisplayModal";
import AgentP2PModal from "@/js/components/P2PDisplayModal/AgentP2PModal";
import PolicyDrawer from "@/js/components/PolicyDrawer";
import RewardModal from "@/js/components/RewardModal";
import Screenshare from "@/components/Screenshare";
import SendNewTabModal from "@/js/components/SendNewTabModal";
import { StartClassButton } from "@/js/components/StartClassButton";
import { TabBar } from "@/js/components/TabBar";
import {
	getSignaturesForFocusResourcePicker,
	transformFocusDtoToEditingFocus,
} from "@/components/FocusLibraryDrawer/FocusResourcePicker/utils";
import { FocusAndFlexibleGridBar } from "@/js/components/RegularClassroom/FocusAndFlexibleGridBar";
import { FocusLibraryDrawer } from "@/components/FocusLibraryDrawer";
import { ActionButton } from "@/js/components/Classrooms/ActionButton";
import ReportsDropdown from "@/js/components/ReportsDropdown";
import ClassToolsDropdown from "@/components/ClassToolsDropdown";
import { PauseInternetDrawer } from "@/js/components/PauseInternetDrawer";
import { envConfig } from "@/config";
import { getFocusedStudentIdsFromAppliedFocuses } from "./utils";
import { ModifyStudentsModal } from "@/components/ModifyStudentsModal";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { ConfirmEndFocusModal } from "@/components/ConfirmEndFocusModal";
import { StudentSortOrderEnum } from "./StudentSortOrder";
import clsx from "clsx";
import { useClassroomLayoutStore } from "@/stores/v2/classroomLayoutStore";
import { ChakraFAIcon } from "@/components/ui/Icon";

export const windowLimit = 3;

export const ClassroomToolsContext = React.createContext({
	openRuleModal: () => {},
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const RegularClassroom = (props) => {
	const navRef = React.useRef();

	const store = useStore();
	const focusStore = useFocusStore();
	const userSessionStore = useUserSessionStore();
	const classroomPolicyStore = useClassroomPolicyStore();
	const screenshareStore = useScreenshareStore();

	const [activeClassroomSourceId] = useState(userSessionStore.getActiveGroup());

	const [view, setView] = useState("screens"); // enum of ('screens', 'tabs')
	const [classOpenTime, setClassOpenTime] = useState(Math.round(Date.now() / 1000));
	const [showAllWindows, setShowAllWindows] = useState(false);
	const features = getFeatures();
	const [classroomPolicy, setClassroomPolicy] = useState(classroomPolicyStore);
	const [signatureDb] = useState(getSignatureDb(store));
	const [nativeAppControlsDb] = useState(getNativeAppControlsDb(store));
	const [classExpiryStr, setClassExpiryStr] = useState("");
	const [isEditingRule, setIsEditingRule] = useState(false);
	const [currentEditingRule, setCurrentEditingRule] = useState({});
	const [currentEditingNativeAppControl, setCurrentEditingNativeAppControl] = useState({});
	const [validationResponseData, setValidationResponseData] = useState({});
	const [studentSortOrder, setStudentSortOrder] = useState(StudentSortOrderEnum.FirstNameAscending);

	// derived
	const [isPauseActive, setIsPauseActive] = useState(classroomPolicyStore.isPauseActive());
	// modals
	const [showBypassModal, setShowBypassModal] = useState(false);
	const [showRewardModal, setShowRewardModal] = useState(false);
	const [showStartClassModal, setShowStartClassModal] = useState(false);
	const [showEditClassModal, setShowEditClassModal] = useState(false);
	const [showPolicyModal, setShowPolicyModal] = useState(false);

	const [showPauseDrawer, setShowPauseDrawer] = useState(false);

	// open site
	const [showOpenSiteModal, setShowOpenSiteModal] = useState(false);
	const [openSiteUser, setOpenSiteUser] = useState(null);

	// policy side drawer
	const [showPolicyDrawer, setShowPolicyDrawer] = useState(false);
	// errors
	const [startClassSessionError, setStartClassSessionError] = useState(false);

	const [p2pModalOpen, setP2pModalOpen] = useState(false);
	const [p2pModalUser, setP2pModalUser] = useState();
	const [p2pAgentId, setP2pAgentId] = useState();
	const [p2pChromeWindowId, setP2pChromeWindowId] = useState();
	const [p2pModalSelectedTab, setP2pModalSelectedTab] = useState();
	const [p2pModalFollowActiveTab, setP2pModalFollowActiveTab] = useState(false);
	const [p2pModalStudentIsFocused, setP2pModalStudentIsFocused] = useState(false);
	const [screenshareModalOpen, setScreenshareModalOpen] = useState(false);

	const modifyStudentsModal = useDisclosure();
	const focusLibraryDrawer = useDisclosure();

	const confirmEndFocusModal = useDisclosure();
	const [confirmEndingFocusName, setConfirmEndingFocusName] = useState("");

	const [users, setUsers] = useState(props.users);
	const [classroom, setClassroom] = useState(props.classroom);

	const tileSize = useClassroomLayoutStore((state) => state.tileSize);
	const [tabItemLimit, setTabItemLimit] = useState(5); // variable tab items to display on student tile

	/**
	 * @type {[import("../../../components/FocusLibraryDrawer/FocusPage").EditingFocus[], React.Dispatch<React.SetStateAction<EditingFocus[]>>]}
	 */
	const [appliedFocuses, setAppliedFocuses] = useState([]);

	const { successToast, errorToast } = useToast();

	const isClassActive = props.isClassStarted;

	// Check if any focuses are applied and update this state when appliedFocuses changes
	const [focusIsApplied, setFocusIsApplied] = useState(false);
	const [focusedStudentIds, setFocusedStudentIds] = useState([]);
	const [activeFocusIsLocked, setActiveFocusIsLocked] = useState(false);

	// When the class becomes active, set the class open time, for reporting to the studentStateStore.
	useEffect(() => {
		setClassOpenTime(Math.round(Date.now() / 1000));
	}, [props.isClassStarted]);

	useEffect(() => {
		setFocusIsApplied(appliedFocuses.length > 0);
		setFocusedStudentIds(getFocusedStudentIdsFromAppliedFocuses(appliedFocuses));
		setActiveFocusIsLocked(appliedFocuses.length > 0 && appliedFocuses[0].tabsLocked === true);
	}, [appliedFocuses]);

	const [isUpdatingFocus, setIsUpdatingFocus] = useState(false);

	function handleEndFocus(focusId) {
		setIsUpdatingFocus(true);
		endFocus(focusId, classroom.stableIdV1, userSessionStore.getTeacherId())
			.then(() => {
				const libItem = focusStore.getLibraryItemById(focusId);
				if (libItem) {
					focusStore.upsertFocusItemInLibrary({ ...libItem, active: false });
				}
				successToast({
					title: "Focus has ended",
					isClosable: true,
				});
				setAppliedFocuses(appliedFocuses.filter((focus) => focus.id !== focusId));
			})
			.catch((err) => {
				console.error("Failed to end focus", err);
				errorToast({
					title: "Failed to end focus",
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			})
			.finally(() => {
				setIsUpdatingFocus(false);
			});
	}

	/**
	 * Handle toggling focus for a student. This function looks at the applied focus and determines whether or not the given student is in the studentIds array.
	 * If the student is in the array, the focus is updated to remove the student from it. If the student is not in the array, the student is added to the focus.
	 * @param {string} studentId - The ID of the student to toggle focus for
	 * @param {string} focusId - The ID of the focus to toggle focus for
	 * @returns {void}
	 */
	function handleToggleFocusForStudent(studentId, focusId) {
		/**
		 * @type {import("../../../components/FocusLibraryDrawer/FocusPage").EditingFocus}
		 */
		const appliedFocus = appliedFocuses.find((focus) => focus.id === focusId);
		if (!appliedFocus) {
			console.error("Focus not found", focusId);
			return;
		}

		const updatedStudentIds = [...appliedFocus.studentIds]; // Make a copy, not a reference
		const studentIndex = updatedStudentIds.indexOf(studentId);
		if (studentIndex >= 0) {
			updatedStudentIds.splice(studentIndex, 1);
		} else {
			updatedStudentIds.push(studentId);
		}

		if (updatedStudentIds.length == 0) {
			setConfirmEndingFocusName(appliedFocus.name);
			confirmEndFocusModal.onOpen();
			return;
		}

		handleUpdateActiveFocus(appliedFocus, updatedStudentIds);
	}

	/**
	 * Used to update the students array for a focus that is currently applied to the classroom.
	 * @param {import("../../../components/FocusLibraryDrawer/FocusPage").EditingFocus} appliedFocus
	 * @param {string[]} studentIds
	 * @returns {void}
	 */
	function handleUpdateActiveFocus(appliedFocus, studentIds) {
		setIsUpdatingFocus(true);

		createOrUpdateFocus(
			appliedFocus.resources,
			appliedFocus.tabsLocked || false,
			true,
			!appliedFocus.isOnTheFly,
			userSessionStore.getTeacherId(),
			appliedFocus.name,
			selectedClassroomStore.stableIdV1,
			studentIds,
			appliedFocus.id
		)
			.then((response) => {
				const updatedFocus = transformFocusDtoToEditingFocus(response, getSignaturesForFocusResourcePicker(store.signatures));
				setAppliedFocuses(appliedFocuses.map((focus) => (focus.id === appliedFocus.id ? updatedFocus : focus)));

				successToast({
					title: "Focus updated",
					isClosable: true,
				});
			})
			.catch((err) => {
				console.error("Failed to update focus", err);
				errorToast({
					title: "Failed to update focus",
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			})
			.finally(() => {
				setIsUpdatingFocus(false);
			});
	}

	const fetchAppliedFocuses = useCallback(() => {
		const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(store.signatures);

		getFocusesAppliedToClass(classroom.stableIdV1, useUserSessionStore.getState().getTeacherId())
			.then((responseBody) => {
				const transformedFocuses = responseBody.appliedFocuses.map((focus) =>
					transformFocusDtoToEditingFocus(focus, allSignatureResourceOptions)
				);
				setAppliedFocuses(transformedFocuses);
			})
			.catch((err) => {
				console.error("Failed to fetch applied focuses", err);
			});
	}, [classroom.stableIdV1, store.signatures]);

	useEffect(() => {
		fetchAppliedFocuses();
	}, [fetchAppliedFocuses]);

	const featureFlagQuery = useFeatureFlagsQuery(useUserSessionStore.getState().active_device);

	const hasClassroomLicense = useLicenseStore((state) => state.classroom);

	const selectedClassroomStore = useSelectedClassroomStore();

	useEffect(() => {
		setUsers(props.users);
		setClassroom(props.classroom);
		setClassExpiryStr(generateSessionStatusTxt(props.classroom.id));
		setStudentSortOrder(props.classroom?.personalisation?.studentSortOrder ?? "firstNameAscending");
	}, [props]);

	useEffect(() => {
		_.debounce((users) => {
			p2pManager.checkUsersToFetchToken(users);
		}, 3000);
	}, [users]);

	// didMount
	useEffect(() => {
		const isChatEnabled = isChatFeatureEnabled();
		if (isChatEnabled) {
			const classroomIdForChat = getClassroomIdForChat(props.classroom);
			store.chat.fetchClassOverview({
				classroomId: classroomIdForChat,
			});
		}

		setClassExpiryStr(generateSessionStatusTxt(props.classroom.id));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isClassStarted]);

	// Fetch max end time only once when component mounts
	useEffect(() => {
		// Only fetch if we don't already have the data
		const currentMaxEndTime = useClassroomSessionStore.getState().localisedMaxEndTime;
		if (!currentMaxEndTime) {
			useClassroomSessionStore.getState().fetchAndPopulateMaxEndTime();
		}
	}, []);

	useEffect(() => {
		const onChangeClassroomPolicy = () => {
			setClassExpiryStr(generateSessionStatusTxt(props.classroom.id));
			setClassroomPolicy(useClassroomPolicyStore.getState());
			setIsPauseActive(useClassroomPolicyStore.getState().isPauseActive());
		};

		const onChangeClassroomSessionStore = (state) => {
			setShowStartClassModal(state.showStartClassModal);
			setShowEditClassModal(state.showEditClassModal);
			setStartClassSessionError(state.startClassSessionError);
			setClassExpiryStr(generateSessionStatusTxt(props.classroom.id));
		};

		const unsubClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(newState) => {
				onChangeClassroomSessionStore(newState);
			}
		);

		const unsubClassroomPolicyStore = useClassroomPolicyStore.subscribe(
			(state) => state,
			() => {
				onChangeClassroomPolicy();
			}
		);

		return () => {
			unsubClassroomPolicyStore();
			unsubClassroomSessionStore();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [features]);

	function handleShowPauseDrawer() {
		setShowPauseDrawer(true);
	}

	const handleShowBypassModal = () => {
		if (!focusIsApplied) {
			setShowBypassModal(true);
		}
	};

	const handleShowRewardModal = () => {
		if (!focusIsApplied) {
			setShowRewardModal(true);
		}
	};

	const handleShowPolicyDrawer = () => {
		setShowPolicyDrawer(true);
	};

	function handleEndPause() {
		unpauseAllStudents(store.classroom.getTeacherStableIdV1(), classroom.stableIdV1)
			.then(() => {
				classroomPolicyStore.setPausedStudents([]);
			})
			.catch(() => {
				errorToast({
					title: "Failed to end pause",
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			});
	}

	const onOpenPolicyModalWithUrl = (isEditingRule, website, signature, user) => {
		let hostname;
		if (website !== undefined) {
			hostname = url.parse(website).hostname;
			if (!hostname) {
				hostname = website;
			}
		}

		setShowPolicyModal(true);
		setIsEditingRule(isEditingRule);
		setCurrentEditingRule({
			website: hostname,
			tag: signature,
			permanent: true,
			user: user,
		});
	};

	const onOpenPolicyModal = (isEditingRule, policy, responseData, type = "SIGNATURE") => {
		setShowPolicyModal(true);
		setIsEditingRule(isEditingRule);
		if (type === "SIGNATURE") {
			setCurrentEditingRule(policy);
			setCurrentEditingNativeAppControl({});
		} else {
			setCurrentEditingRule({});
			setCurrentEditingNativeAppControl(policy);
		}
		setValidationResponseData(responseData);
	};

	const handleShowManageStudentsModal = () => {
		modifyStudentsModal.onOpen();
	};

	const handleShowScreenshareModal = () => {
		setScreenshareModalOpen(true);
	};

	const handleShowFocusDrawer = () => {
		focusLibraryDrawer.onOpen();
	};

	const handleClosePolicyModal = () => {
		setShowPolicyModal(false);
		setIsEditingRule(false);
		setCurrentEditingRule({});
	};

	const toggleShowWindows = () => setShowAllWindows(!showAllWindows);

	const renderBypassActiveButton = () => {
		const isBypassActive = moment().unix() < classroomPolicy.bypass_active_until;
		if (features.indexOf("bypass_code") && !focusIsApplied && isBypassActive) {
			return (
				<Box
					display="flex"
					marginLeft={0}
					columnGap={3}
					title="Bypass Code"
					className="ctxbar_button--green"
					onClick={handleShowBypassModal}
				>
					<ChakraFAIcon icon="fa-key" variant="solid" />
					<span>Bypass Code</span>
				</Box>
			);
		}
	};

	const renderRewardActiveButton = () => {
		const isRewardActive = moment().unix() < classroomPolicy.reward_active_until;
		if (features.includes("reward") && !focusIsApplied && isRewardActive) {
			return (
				<Box display="flex" marginLeft={0} columnGap={3} title="Reward" className={"ctxbar_button--green"} onClick={handleShowRewardModal}>
					<ChakraFAIcon icon="fa-gift" variant="solid" />
					<span>Reward</span>
				</Box>
			);
		}
	};

	const policySize = classroomPolicy.blocked.length + classroomPolicy.allowed.length + classroomPolicy.nativeAppControls.length;

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const forceCloseTab = (tabId, user, setLoading = false) => {
		if (!features.includes("close_tab")) return;

		const [windowId, tab] = tabId.split("_");

		Api.post("/event/close-tab", {
			recipients: [user.username],
			event_object: {
				windowId: windowId.toString(),
				tabId: tab.toString(),
			},
		});
	};

	const openP2pModal = (user, tabId, peerAgentId, chromeWindowId, followActiveTab = false) => {
		setP2pModalUser(user.username);
		setP2pModalSelectedTab(tabId);
		setP2pAgentId(peerAgentId);
		setP2pChromeWindowId(chromeWindowId);
		setP2pModalOpen(true);
		setP2pModalFollowActiveTab(followActiveTab);
		setP2pModalStudentIsFocused(focusedStudentIds.includes(user.stableIdV1));
	};

	const openOpenSiteModal = (user) => {
		setOpenSiteUser(user);
		setShowOpenSiteModal(true);
	};

	const closeOpenSiteModal = () => {
		setOpenSiteUser(null);
		setShowOpenSiteModal(false);
	};

	const sendNewTabModalTitle = () => {
		if (openSiteUser) return `Open a site for ${userDisplayText(openSiteUser)}`;
		return "";
	};

	const closeP2pModal = () => {
		setP2pModalOpen(false);
	};

	const renderP2pModal = () => {
		const user = users.find((user) => user.username === p2pModalUser);
		const window = user.screenshots.find((screen) => screen.chrome_id === p2pAgentId && screen.chrome_window_id === p2pChromeWindowId);
		const platform = windowUtils.getPlatformType(window);
		const chrome = platform === "Chromebook";

		if (!window || !user || (!p2pModalSelectedTab && chrome)) return;
		if (!chrome && !store.featureToggles.data["agent-p2p-screenshots"]) return;

		const studentIsFocused = p2pModalStudentIsFocused;

		if (chrome) {
			const tabs = [
				{
					tab_id: window.tab_id,
					favIcon: window.favicon,
					tabUrl: window.url,
					title: window.title,
				},
				...window.background_tabs,
			];
			const startTabIndex = tabs.findIndex((tab) => tab.tab_id === p2pModalSelectedTab);

			return (
				<P2PDisplayModal
					closeTab={(tabId, windowId) => forceCloseTab(tabId, windowId, user, false)}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					startTab={tabs[startTabIndex]}
					startTabIndex={startTabIndex}
					peerAgentId={p2pAgentId}
					activeTab={window}
					tabs={tabs}
					window_data={user.window_data}
					openRuleModal={onOpenPolicyModalWithUrl}
					windowId={p2pChromeWindowId}
					classroom={classroom}
					showOpenTabModal={openOpenSiteModal}
					followActiveTab={p2pModalFollowActiveTab}
					platform={platform}
					studentIsFocused={studentIsFocused}
				/>
			);
		} else {
			return (
				<AgentP2PModal
					isMonitoringClass={false}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					activeTab={window}
					windowId={p2pChromeWindowId}
					peerAgentId={p2pAgentId}
					classroom={classroom}
					platform={platform}
					showOpenTabModal={openOpenSiteModal}
					openRuleModal={onOpenPolicyModalWithUrl}
					studentIsFocused={studentIsFocused}
					window_data={user.window_data}
				/>
			);
		}
	};

	const classroomStore = useStore(
		(state) => ({
			setStudentSortOrder: state.classroom.setStudentSortOrder,
		}),
		shallow
	);

	const onSortOrderChange = (sortOrder) => {
		setStudentSortOrder(sortOrder);
		classroomStore.setStudentSortOrder(classroom.id, sortOrder);
	};

	let totalWindows = 0;
	let shownWindows = 0;
	let extraWindows = 0;
	let sortedUsers = sortUsersByActivity(users, classroomPolicy.locked_users, studentSortOrder === StudentSortOrderEnum.LastNameAscending);
	sortedUsers = selectedClassroomStore.sortUsersByPin(sortedUsers);

	let elements = [];
	for (const user of sortedUsers) {
		let ret = [];
		const uniqueDevices = Array.from(new Set(user.screenshots.map((windowItem) => windowItem.chrome_id))).sort();
		for (const windowData of user.screenshots) {
			ret.push(
				<RegularClassroomUser
					key={user.username + "_" + windowData.chrome_id + "_" + windowData.chrome_window_id}
					deviceIndex={uniqueDevices.indexOf(windowData.chrome_id) + 1}
					user={user}
					windowData={windowData}
					view={view}
					signatureDb={signatureDb}
					showStudentAnnouncementBtn={features.includes("message_student")}
					showOpenTabBtn={features.includes("open_tab")}
					classIsActive={isClassActive}
					canCloseTab={features.includes("close_tab")}
					classroom={classroom}
					openP2pModal={openP2pModal}
					openNewTabModal={openOpenSiteModal}
					tabItemLimit={tabItemLimit}
					studentsAreFocused={appliedFocuses.length > 0}
					studentIsFocused={focusedStudentIds.includes(user.stableIdV1)}
					handleToggleFocusForStudent={(studentId) => handleToggleFocusForStudent(studentId, appliedFocuses[0].id)}
					isUpdatingFocus={isUpdatingFocus}
					activeFocusIsLocked={activeFocusIsLocked}
					studentIsBeingScreenshared={screenshareStore.screenshareStudentUsernames.has(user.username)}
					classOpenTime={classOpenTime}
				/>
			);
		}

		if (ret.length === 0) {
			ret.push(
				<RegularClassroomUser
					key={user.username}
					user={user}
					windowData={{}}
					view={view}
					signatureDb={signatureDb}
					showStudentAnnouncementBtn={features.includes("message_student")}
					showOpenTabBtn={features.includes("open_tab")}
					classIsActive={isClassActive}
					classroom={classroom}
					openNewTabModal={openOpenSiteModal}
					studentsAreFocused={appliedFocuses.length > 0}
					studentIsFocused={focusedStudentIds.includes(user.stableIdV1)}
					handleToggleFocusForStudent={(studentId) => handleToggleFocusForStudent(studentId, appliedFocuses[0].id)}
					isUpdatingFocus={isUpdatingFocus}
					activeFocusIsLocked={activeFocusIsLocked}
					studentIsBeingScreenshared={screenshareStore.screenshareStudentUsernames.has(user.username)}
					classOpenTime={classOpenTime}
				/>
			);
		}
		const knownDevices = [];
		let lastUniqueDeviceIndex = 0;
		for (const screenIndex in ret) {
			const currentScreen = ret[screenIndex];
			if (currentScreen.props.deviceIndex && knownDevices.indexOf(currentScreen.props.deviceIndex) < 0) {
				knownDevices.push(currentScreen.props.deviceIndex);
				arrayMove(ret, screenIndex, lastUniqueDeviceIndex);
				lastUniqueDeviceIndex++;
			}
		}
		totalWindows += ret.length;
		if (!showAllWindows && ret.length >= windowLimit) {
			ret = ret.slice(0, windowLimit);
		}
		if (showAllWindows && ret.length >= windowLimit) {
			extraWindows += ret.length - windowLimit;
		}
		shownWindows += ret.length;

		elements = [...elements, ...ret];
	}

	// Disable pin animations for the first 500ms to prevent unnecessary animations on page rerender
	const [pinAnimationsEnabled, setPinAnimationsEnabled] = useState(false);
	useEffect(() => {
		setTimeout(() => {
			setPinAnimationsEnabled(true);
		}, 500);
	}, []);

	const maxStudents = envConfig.MAX_STUDENTS;

	return (
		<Box>
			<Nav selectedClass={classroom} />
			<TabBar />

			{!hasClassroomLicense ? (
				<ClassroomPromo />
			) : (
				<ErrorBoundary>
					<ul className="ctxbar--classroomPage">
						<li
							className={
								isClassActive
									? view === "screens"
										? "ctxbar-toggle--active"
										: "ctxbar-toggle track-ScreenView_Active"
									: "ctxbar-toggle-hide"
							}
							onClick={() => setView("screens")}
						>
							Screen view
						</li>
						<li
							className={
								isClassActive
									? view === "tabs"
										? "ctxbar-toggle--active--list"
										: "ctxbar-toggle--list track-ListView_Active"
									: "ctxbar-toggle-hide"
							}
							onClick={() => setView("tabs")}
						>
							List view
						</li>

						{isClassActive ? (
							<EditClassButton
								classroomName={classroom.sourceId}
								classExpiryStr={classExpiryStr}
								isMonitoringClass={false}
								screenView={true}
								classActive={isClassActive}
								classroomId={classroom.id}
							>
								<span className="StatusDot Classroom_status" title="Classroom active" />
								Class until {classExpiryStr}
							</EditClassButton>
						) : (
							<StartClassButton classroomName={classroom.sourceId} disabled={props.users.length > maxStudents} />
						)}

						{isClassActive && <EndClassButton classroomName={classroom.sourceId} isMonitoringClass={false} />}

						<Flex columnGap={2} ml={"auto"}>
							{featureFlagQuery.data?.includes("SCREEN_SHARE") && (
								<Screenshare
									open={screenshareModalOpen}
									classroomSourceId={activeClassroomSourceId}
									handleClose={() => setScreenshareModalOpen(false)}
									users={users}
									setModalOpen={setScreenshareModalOpen}
									classroomId={classroom.id}
									focusedStudentIds={focusedStudentIds}
								/>
							)}
							{features.includes("lock") && isPauseActive && (
								<Tooltip label="End Pause for all students" placement={"top"} hasArrow shouldWrapChildren>
									<ActionButton icon={"fa-pause"} label={`End Pause`} handleClick={handleEndPause} danger testId="end-pause-button" />
								</Tooltip>
							)}

							{renderBypassActiveButton()}
							{renderRewardActiveButton()}

							<ReportsDropdown students={users} />

							<ClassToolsDropdown
								openPauseModal={handleShowPauseDrawer}
								openFocusDrawer={handleShowFocusDrawer}
								openRewardModal={handleShowRewardModal}
								openBypassModal={handleShowBypassModal}
								openManageStudentsModal={handleShowManageStudentsModal}
								openScreenshareModal={handleShowScreenshareModal}
								isClassActive={isClassActive}
								areAnyStudentsFocused={focusedStudentIds.length > 0}
								areAllStudentsFocused={focusedStudentIds.length === users.length}
								studentsArePaused={isPauseActive}
								classHasStudents={users.length > 0}
								classSourceId={userSessionStore.getActiveGroup()}
							/>

							{features.includes("policies") && (
								<ActionButton
									icon={"fa-pen-to-square"}
									label={`${policySize} Rule${policySize === 1 ? "" : "s"}`}
									handleClick={handleShowPolicyDrawer}
									disabled={focusIsApplied}
									testId="rules-button"
								/>
							)}

							<ErrorBoundary>
								<ClassroomChat classroom={classroom} navRef={navRef} />
							</ErrorBoundary>
						</Flex>
						<PolicyDrawer
							open={showPolicyDrawer}
							signatureDb={signatureDb}
							nativeAppControlsDb={nativeAppControlsDb}
							policy={classroomPolicy}
							nativeAppControls={classroomPolicy.nativeAppControls}
							onClose={() => setShowPolicyDrawer(false)}
							onOpenCreatePolicy={onOpenPolicyModal}
							users={users}
						/>
					</ul>

					<FocusAndFlexibleGridBar
						studentCount={props.users.length}
						setTabItemLimit={setTabItemLimit}
						appliedFocuses={appliedFocuses}
						isUpdatingFocus={isUpdatingFocus}
						endFocus={handleEndFocus}
						studentSortOrder={studentSortOrder}
						onSortOrderChange={onSortOrderChange}
						classIsActive={isClassActive}
						bubbleUpdatedFocus={(focus) => {
							setAppliedFocuses([focus]);
						}}
					/>

					<ClassroomToolsContext.Provider value={{ openRuleModal: onOpenPolicyModalWithUrl }}>
						{/*Pinning animation*/}
						<FlipMove
							className={clsx("RegularClassroom_students", {
								StudentTile_grid_med: tileSize === "lg",
								StudentTile_grid_small: tileSize === "sm",
							})}
							disableAllAnimations={!pinAnimationsEnabled}
							data-testid="flip-move"
							data-test-active={pinAnimationsEnabled}
						>
							{elements}
						</FlipMove>
					</ClassroomToolsContext.Provider>

					<CreatePolicy
						open={showPolicyModal}
						signatureDb={signatureDb}
						nativeAppControlsDb={nativeAppControlsDb}
						editing={isEditingRule}
						policy={currentEditingRule}
						nativeAppControl={currentEditingNativeAppControl}
						onClose={handleClosePolicyModal}
						validationResponseData={validationResponseData}
						users={users}
					/>
					<RewardModal open={showRewardModal} signatureDb={signatureDb} onClose={() => setShowRewardModal(false)} />
					<BypassModal open={showBypassModal} onClose={() => setShowBypassModal(false)} />
					<PauseInternetDrawer
						isOpen={showPauseDrawer}
						closeDrawer={() => setShowPauseDrawer(false)}
						classId={classroom.stableIdV1}
						students={users}
						pausedStudentIds={classroomPolicy.locked_users}
					/>

					<ClassroomStartSession open={showStartClassModal} onStartError={startClassSessionError} inClassroom studentCount={users.length} />

					<ClassroomEditSession
						open={showEditClassModal}
						onExtendError={startClassSessionError}
						durationPlaceholder={classExpiryStr}
						inClassroom
					/>

					<SendNewTabModal
						open={showOpenSiteModal}
						title={sendNewTabModalTitle()}
						classroomSourceId={activeClassroomSourceId}
						users={[openSiteUser]}
						onClose={closeOpenSiteModal}
					/>

					<ModifyStudentsModal
						isOpen={modifyStudentsModal.isOpen}
						onClose={modifyStudentsModal.onClose}
						classroom={classroom}
						students={users}
						isMonitoringClass={false}
						// When a student is removed from a class, any focus applied to them is lifted.
						// If they are re-added to the class, the teacher will need to reapply the focus.
						// This is not reflected on the UI unless we refresh the applied focuses - the student
						// will appear focused when they are not until the state held by the UI is updated.
						studentsModifiedCallback={() => fetchAppliedFocuses()}
					/>

					<FocusLibraryDrawer
						bubbleAppliedOrUpdatedFocus={(focusDto) => {
							const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(store.signatures);
							setAppliedFocuses([...appliedFocuses, transformFocusDtoToEditingFocus(focusDto, allSignatureResourceOptions)]);
						}}
						isOpen={focusLibraryDrawer.isOpen}
						closeDrawer={focusLibraryDrawer.onClose}
						classIsActive={isClassActive}
					/>

					<ConfirmEndFocusModal
						isOpen={confirmEndFocusModal.isOpen}
						onClose={confirmEndFocusModal.onClose}
						focusName={confirmEndingFocusName}
						handleConfirmation={() => handleEndFocus(appliedFocuses[0].id)}
					/>

					{p2pModalOpen && renderP2pModal()}
					{totalWindows - shownWindows > 0 || extraWindows > 0 ? (
						<div className="RegularClassroom_showAllWindowsButton" onClick={toggleShowWindows}>
							{showAllWindows ? "Hide (" + extraWindows + ") Extra Screens" : "Show (" + (totalWindows - shownWindows) + ") Extra Screens"}
						</div>
					) : null}
				</ErrorBoundary>
			)}
		</Box>
	);
};

RegularClassroom.propTypes = {
	users: PropTypes.array.isRequired,
	classroom: PropTypes.object,
	router: PropTypes.object,
	isClassStarted: PropTypes.bool,
};
